import type {
  PlanReviewBoundingBox,
  PlanReviewVisualBox,
  PlanReviewVisualIntelligencePayload,
  PlanReviewVisualItemProgress,
} from "@/api/plan_review";

function isVisualBox(value: unknown): value is PlanReviewVisualBox {
  if (!value || typeof value !== "object") return false;
  const box = value as PlanReviewVisualBox;
  return [box.x, box.y, box.w, box.h].every((part) => typeof part === "number" && Number.isFinite(part));
}

export function xywhToBbox(box: PlanReviewVisualBox): [number, number, number, number] {
  return [box.x, box.y, box.x + box.w, box.y + box.h];
}

export function applicableVisualPages(vi: PlanReviewVisualIntelligencePayload | null | undefined): string[] {
  if (!vi?.pages) return [];
  return Object.entries(vi.pages)
    .filter(([, page]) => page?.context_written === true)
    .map(([number]) => number);
}

export function visualItemProgress(
  vi: PlanReviewVisualIntelligencePayload | null | undefined,
  itemKey: string,
  pageNumbers: string[]
): PlanReviewVisualItemProgress {
  if (vi?.error?.message) {
    return { key: itemKey, status: "error", message: vi.error.message };
  }

  const pages = vi?.items?.[itemKey]?.pages || {};
  const relevant = pageNumbers.length > 0 ? pageNumbers : Object.keys(pages);
  if (relevant.length === 0) return { key: itemKey, status: "pending" };

  const entries = relevant.map((number) => pages[number]);
  const errored = entries.filter((entry) => entry?.error);
  if (errored.length === relevant.length) {
    return { key: itemKey, status: "error", message: errored[0]?.error?.message };
  }

  const extractedCount = entries.filter((entry) => entry?.extracted).length;
  const finderCount = entries.filter((entry) => Array.isArray(entry?.finder_boxes)).length;

  if (extractedCount === relevant.length) return { key: itemKey, status: "extracted" };
  if (extractedCount > 0) return { key: itemKey, status: "extracting" };
  if (finderCount > 0) return { key: itemKey, status: "finding" };
  return { key: itemKey, status: "pending" };
}

function pushBoxes(
  boxes: PlanReviewBoundingBox[],
  itemKey: string,
  label: string,
  page: number,
  stage: "finder" | "extracted",
  source: unknown[] | undefined,
  stream: boolean
) {
  (source || []).filter(isVisualBox).forEach((box, index) => {
    boxes.push({
      page,
      bbox: xywhToBbox(box),
      label,
      focusKey: `item:${itemKey}:vi:${stage}:${index}`,
      stage,
      stream,
    });
  });
}

export function boxesFromVisualIntelligence(
  vi: PlanReviewVisualIntelligencePayload | null | undefined,
  labelsByKey: Record<string, string>,
  stream: boolean
): PlanReviewBoundingBox[] {
  if (!vi?.items) return [];

  const boxes: PlanReviewBoundingBox[] = [];
  Object.entries(vi.items).forEach(([itemKey, item]) => {
    const label = labelsByKey[itemKey] || itemKey;
    Object.entries(item?.pages || {}).forEach(([pageKey, entry]) => {
      const page = Number(pageKey);
      if (!Number.isFinite(page)) return;

      const extractedBoxes = (entry.extracted?.crops || []).flatMap((crop) => crop.boxes || []);
      if (extractedBoxes.some(isVisualBox)) {
        pushBoxes(boxes, itemKey, label, page, "extracted", extractedBoxes, stream);
        return;
      }

      const finderBoxes = (entry.finder_boxes || []).flatMap((finder) => finder.boxes || []);
      pushBoxes(boxes, itemKey, label, page, "finder", finderBoxes, stream);
    });
  });
  return boxes;
}
